import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Dropdown } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { getVehicles } from '../services/vehicleService';
import { getBrands, getModelsByBrand } from '../services/vehicleDataService';
import { DEFAULT_FILTER_STATE } from '../constants/filterConstants';
import { parseUrlParamsToFilters, filtersToUrlParams, getCleanFilters } from '../utils/filterUtils';

/**
 * Araçlar listesi sayfası
 * MongoDB'den gelen araç verilerini görüntüler ve filtreleme yapar
 */
const VehiclesPage = () => {
    const location = useLocation();

    // Durum değişkenleri
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filtre seçenekleri için durum değişkenleri
    const [brands, setBrands] = useState([]);
    const [models, setModels] = useState([]);
    const [loadingDropdowns, setLoadingDropdowns] = useState(true);

    // Filtre değerleri - varsayılan değerlerle başlat
    const [filters, setFilters] = useState(DEFAULT_FILTER_STATE);

    // Sıralama için durum değişkeni
    const [sortOption, setSortOption] = useState('');

    // Sıralama seçenekleri
    const sortOptions = [
        { value: 'price_asc', label: 'En Düşük Fiyat' },
        { value: 'price_desc', label: 'En Yüksek Fiyat' },
        { value: 'year_desc', label: 'Yeniden Eskiye' },
        { value: 'year_asc', label: 'Eskiden Yeniye' }
    ];

    /**
     * Araçları belirtilen filtrelere göre yükler
     * @param {Object} currentFilters - Kullanılacak filtre değerleri
     */
    const fetchVehicles = async (currentFilters = filters) => {
        try {
            setLoading(true);

            // Yardımcı fonksiyonla boş olmayan filtreleri al
            const validFilters = getCleanFilters(currentFilters);
            console.log("Sending filters to API:", validFilters);

            // Sıralama seçeneğini filtrelere ekle
            if (sortOption) {
                validFilters.sort = sortOption;
            }

            const result = await getVehicles(validFilters);

            if (result.success) {
                setVehicles(result.vehicles);
            } else {
                setError('Araçlar yüklenirken bir hata oluştu');
            }
        } catch (error) {
            setError('Sunucu hatası');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // Sayfa ilk yüklendiğinde URL'den filtreleri al ve filtre seçeneklerini yükle
    useEffect(() => {
        // Dropdown verileri yükle
        const loadFilterOptions = async () => {
            try {
                setLoadingDropdowns(true);

                // Markaları yükle
                const brandsResponse = await getBrands();

                if (brandsResponse.success) {
                    setBrands(brandsResponse.data);
                }
            } catch (error) {
                console.error('Filtre seçenekleri yüklenirken hata:', error);
            } finally {
                setLoadingDropdowns(false);
            }
        };

        loadFilterOptions();

        // URL'den query parametrelerini al
        const params = new URLSearchParams(location.search);
        console.log("URL parameters:", Object.fromEntries(params.entries()));

        // Yardımcı fonksiyonla parametreleri filtre nesnesine dönüştür
        const parsedFilters = parseUrlParamsToFilters(params);
        console.log("Parsed filters:", parsedFilters);

        // Sıralama seçeneğini kontrol et
        if (params.get('sort')) {
            setSortOption(params.get('sort'));
        }

        // Filtre state'ini güncelle
        setFilters(parsedFilters);

        // Araçları bu filtrelerle yükle
        fetchVehicles(parsedFilters);
    }, [location.search]); // URL değiştiğinde tekrar çalıştır

    // Seçilen markaya göre modelleri yükle
    useEffect(() => {
        const loadModels = async () => {
            if (!filters.brand) {
                setModels([]);
                return;
            }

            try {
                const response = await getModelsByBrand(filters.brand);
                if (response.success) {
                    console.log("Loaded models for brand:", filters.brand, response.data);
                    setModels(response.data);

                    // Eğer bodyType zaten seçilmişse, modellerden kontrol et
                    if (filters.bodyType) {
                        const hasMatchingModel = response.data.some(model =>
                            model.type && model.type.toLowerCase() === filters.bodyType.toLowerCase()
                        );
                        console.log("Has matching bodyType in models:", hasMatchingModel);

                        // Eğer seçili bodyType modellerde yoksa, onu manuel olarak ekle
                        if (!hasMatchingModel) {
                            console.log("Adding bodyType as static option:", filters.bodyType);
                        }
                    }
                }
            } catch (error) {
                console.error(`${filters.brand} markasına ait modeller yüklenirken hata:`, error);
                setModels([]);
            }
        };

        loadModels();
    }, [filters.brand, filters.bodyType]);

    /**
     * Filtre değişikliklerini işler
     * @param {Event} e - Form olayı
     */
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        console.log(`Filter changed: ${name} = ${value}`);

        // Marka değiştiğinde model ve bodyType değerlerini sıfırla
        if (name === 'brand') {
            setFilters(prev => ({
                ...prev,
                model: '',
                bodyType: '',
                [name]: value
            }));
        }
        // Model değiştiğinde, seçilen modelin kasa tipini otomatik olarak doldur
        else if (name === 'model' && value) {
            const selectedModel = models.find(model => model.name === value);
            if (selectedModel && selectedModel.type) {
                setFilters(prev => ({
                    ...prev,
                    bodyType: selectedModel.type,
                    [name]: value
                }));
            } else {
                setFilters(prev => ({
                    ...prev,
                    [name]: value
                }));
            }
        } else {
            setFilters(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    /**
     * Sıralama seçeneği değişikliğini işler
     * @param {string} option - Seçilen sıralama seçeneği
     */
    const handleSortChange = (option) => {
        setSortOption(option);

        // Araçları hemen sırala (sayfa içinde)
        let sortedVehicles = [...vehicles];

        switch (option) {
            case 'price_asc':
                sortedVehicles.sort((a, b) => (a.price || 0) - (b.price || 0));
                break;
            case 'price_desc':
                sortedVehicles.sort((a, b) => (b.price || 0) - (a.price || 0));
                break;
            case 'year_desc':
                sortedVehicles.sort((a, b) => (b.year || 0) - (a.year || 0));
                break;
            case 'year_asc':
                sortedVehicles.sort((a, b) => (a.year || 0) - (b.year || 0));
                break;
            default:
                // Varsayılan sıralama yok
                break;
        }

        setVehicles(sortedVehicles);

        // URL'yi güncelle
        const updatedFilters = { ...filters, sort: option };
        const queryParams = filtersToUrlParams(updatedFilters).toString();
        window.history.replaceState(null, '', `${location.pathname}?${queryParams}`);
    };

    /**
     * Filtre formunu gönderir ve araçları filtrelere göre yeniden yükler
     * @param {Event} e - Form gönderme olayı
     */
    const handleSearch = (e) => {
        e.preventDefault();
        console.log("Submitting filters:", filters);

        // Sıralama seçeneğini de ekle
        const updatedFilters = { ...filters };
        if (sortOption) {
            updatedFilters.sort = sortOption;
        }

        // URL'yi güncel filtrelerle güncelle (sayfa yenilenmeden)
        const queryParams = filtersToUrlParams(updatedFilters).toString();
        console.log("Query params:", queryParams);

        // history.pushState kullanarak URL'yi güncelle, sayfayı yenilemeden
        window.history.replaceState(null, '', `${location.pathname}?${queryParams}`);

        // Verileri filtrelerle yükle
        fetchVehicles(updatedFilters);
    };

    /**
     * Cloudinary URL'sini boyutlandırmak için yardımcı fonksiyon
     * @param {string} imageUrl - Resim URL'si
     * @param {number} width - İstenen genişlik
     * @param {number} height - İstenen yükseklik
     * @returns {string} - Optimize edilmiş resim URL'si
     */
    // Cloudinary URL'sini boyutlandırmak için yardımcı fonksiyon
    const getOptimizedImageUrl = (imageUrl, width = 600, height = 400) => {
        // Eğer resim yoksa yerel default resmi döndür
        if (!imageUrl) return "/uploads/vehicles/default.jpg";

        // Eğer Cloudinary URL'si ise optimize et (Cloudinary default URL'sini ENGELLE)
        if (imageUrl.includes('cloudinary.com')) {
            if (imageUrl.includes('default_zxlbnq.webp')) {
                return "/uploads/vehicles/default.jpg";
            }

            try {
                const parts = imageUrl.split('/upload/');
                if (parts.length !== 2) return imageUrl;

                return `${parts[0]}/upload/w_${width},h_${height},c_fill,q_auto/${parts[1]}`;
            } catch (error) {
                console.error('URL işleme hatası:', error);
                return imageUrl;
            }
        }

        // Eğer yerel URL değilse ve tam URL formatında değilse
        if (!imageUrl.startsWith('http')) {
            return `${imageUrl}`;
        }

        return imageUrl;
    };

    /**
     * Araç için en iyi resmi seçer
     * @param {Object} vehicle - Araç nesnesi
     * @returns {string} - Optimize edilmiş resim URL'si
     */
    const getVehicleImage = (vehicle) => {
        if (!vehicle) return getOptimizedImageUrl(null);

        if (vehicle.images && vehicle.images.length > 0) {
            return getOptimizedImageUrl(vehicle.images[0]);
        }

        if (vehicle.imageUrl) {
            return getOptimizedImageUrl(vehicle.imageUrl);
        }

        return getOptimizedImageUrl(null);
    };

    /**
     * Seçilen markaya ait kasa tipleri ve URL'den gelen kasa tipi
     * @returns {Array} - Benzersiz kasa tipleri dizisi
     */
    const getBodyTypesForBrand = () => {
        // Eğer model yoksa boş dizi döndür
        if (!models.length) {
            // URL'den bodyType seçili ise, onu statik olarak ekle
            if (filters.bodyType) {
                return [filters.bodyType];
            }
            return [];
        }

        // Modellerdeki benzersiz kasa tipleri
        let uniqueTypes = [...new Set(models.filter(model => model.type).map(model => model.type))];

        // Eğer URL'den gelen bodyType seçili ise ve modellerde yoksa, onu da ekle
        if (filters.bodyType && !uniqueTypes.includes(filters.bodyType)) {
            uniqueTypes.push(filters.bodyType);
        }

        console.log("Available body types:", uniqueTypes);
        return uniqueTypes;
    };

    // Aktif sıralama seçeneğinin etiketini bul
    const getActiveSortLabel = () => {
        const option = sortOptions.find(opt => opt.value === sortOption);
        return option ? option.label : 'Sırala';
    };

    return (
        <Container className="py-5">
            <h1 className="mb-4">Araçlar</h1>

            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Body>
                            <Card.Title>Filtreler</Card.Title>
                            <Form onSubmit={handleSearch}>
                                <Row>
                                    {/* Marka Dropdown */}
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Marka</Form.Label>
                                            <Form.Select
                                                name="brand"
                                                value={filters.brand || ''}
                                                onChange={handleFilterChange}
                                                disabled={loadingDropdowns}
                                            >
                                                <option value="">Tüm Markalar</option>
                                                {brands.map(brand => (
                                                    <option key={brand} value={brand}>
                                                        {brand}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    {/* Model Dropdown */}
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Model</Form.Label>
                                            <Form.Select
                                                name="model"
                                                value={filters.model || ''}
                                                onChange={handleFilterChange}
                                                disabled={!filters.brand || models.length === 0}
                                            >
                                                <option value="">Tüm Modeller</option>
                                                {models.map(model => (
                                                    <option key={model.name} value={model.name}>
                                                        {model.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    {/* Fiyat Filtreleri */}
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Min. Fiyat</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="minPrice"
                                                value={filters.minPrice || ''}
                                                onChange={handleFilterChange}
                                                placeholder="Min. Fiyat"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Max. Fiyat</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="maxPrice"
                                                value={filters.maxPrice || ''}
                                                onChange={handleFilterChange}
                                                placeholder="Max. Fiyat"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    {/* Yıl Filtreleri */}
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Min. Yıl</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="minYear"
                                                value={filters.minYear || ''}
                                                onChange={handleFilterChange}
                                                placeholder="Min. Yıl"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Max. Yıl</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="maxYear"
                                                value={filters.maxYear || ''}
                                                onChange={handleFilterChange}
                                                placeholder="Max. Yıl"
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Yakıt Tipi Dropdown */}
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Yakıt Tipi</Form.Label>
                                            <Form.Select
                                                name="fuelType"
                                                value={filters.fuelType || ''}
                                                onChange={handleFilterChange}
                                            >
                                                <option value="">Tümü</option>
                                                <option value="Benzin">Benzin</option>
                                                <option value="Dizel">Dizel</option>
                                                <option value="LPG">LPG</option>
                                                <option value="Elektrik">Elektrik</option>
                                                <option value="Hibrit">Hibrit</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    {/* Vites Dropdown */}
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Vites</Form.Label>
                                            <Form.Select
                                                name="transmission"
                                                value={filters.transmission || ''}
                                                onChange={handleFilterChange}
                                            >
                                                <option value="">Tümü</option>
                                                <option value="Manuel">Manuel</option>
                                                <option value="Otomatik">Otomatik</option>
                                                <option value="Yarı Otomatik">Yarı Otomatik</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    {/* Kasa Tipi Dropdown */}
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Kasa Tipi</Form.Label>
                                            <Form.Select
                                                name="bodyType"
                                                value={filters.bodyType || ''}
                                                onChange={handleFilterChange}
                                            >
                                                <option value="">Tümü</option>
                                                {getBodyTypesForBrand().map(type => (
                                                    <option
                                                        key={type}
                                                        value={type.toLowerCase()}
                                                    >
                                                        {type}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <small className="text-muted">
                                                Seçili değer: {filters.bodyType || 'Seçilmedi'}
                                            </small>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6} className="d-flex align-items-end">
                                        <Button variant="primary" type="submit" className="mb-3 me-2">
                                            Filtrele
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Sıralama ve Sonuç Başlığı */}
            <Row className="align-items-center mb-3">
                <Col xs={6}>
                    <h5 className="mb-0">{vehicles.length} Araç Bulundu</h5>
                </Col>
                <Col xs={6} className="text-end">
                    <Dropdown onSelect={handleSortChange}>
                        <Dropdown.Toggle variant="outline-secondary" id="dropdown-sort">
                            {getActiveSortLabel()}
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end">
                            {sortOptions.map(option => (
                                <Dropdown.Item
                                    key={option.value}
                                    eventKey={option.value}
                                    active={sortOption === option.value}
                                >
                                    {option.label}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                </Col>
            </Row>

            {/* Araçları görüntüleme bölümü */}
            {loading ? (
                <div className="text-center my-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Yükleniyor...</span>
                    </Spinner>
                </div>
            ) : error ? (
                <div className="alert alert-danger">{error}</div>
            ) : vehicles.length === 0 ? (
                <div className="alert alert-info">Araç bulunamadı</div>
            ) : (
                <Row>
                    {vehicles.map((vehicle) => (
                        <Col key={vehicle._id} md={4} className="mb-4">
                            <Card className="h-100 shadow-sm">
                                {/* Resim kapsayıcısı - sabit yükseklik ve object-fit */}
                                <div style={{ height: '200px', overflow: 'hidden' }}>
                                    <Card.Img
                                        variant="top"
                                        src={getVehicleImage(vehicle)}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = getOptimizedImageUrl(null);
                                        }}
                                        alt={vehicle.title || `${vehicle.brand} ${vehicle.model}`}
                                        style={{ objectFit: 'cover', height: '100%', width: '100%' }}
                                    />
                                </div>
                                <Card.Body className="d-flex flex-column">
                                    <Card.Title>{vehicle.title || `${vehicle.brand} ${vehicle.model}`}</Card.Title>
                                    <Card.Text>
                                        {vehicle.year} Model, {vehicle.transmission}, {vehicle.fuelType}
                                        {vehicle.bodyType && `, ${vehicle.bodyType}`}
                                    </Card.Text>
                                    <Card.Text className="fw-bold">
                                        {vehicle.price?.toLocaleString()} ₺
                                    </Card.Text>
                                    <div className="mt-auto">
                                        <Button
                                            as={Link}
                                            to={`/vehicles/${vehicle._id}`}
                                            variant="primary"
                                            className="w-100"
                                        >
                                            Detaylar
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
        </Container>
    );
};

export default VehiclesPage;